import {IAlternativeResult, IMonteCarloProcessedData} from '@/modules/lcca/interfaces/probabilistic';
// @ts-ignore
import {ILccaCalculatedImperial} from '@/modules/lcca/interfaces/lccaCalculated';
// @ts-ignore
import {pickColors} from '@/modules/lcca/lib/pickColors';

const d3 = {

  ...require('d3-shape'),
  ...require('d3-selection'),
  ...require('d3-axis'),
  ...require('d3-scale'),
  ...require('d3-array'),
  ...require('d3-drag'),
};

export const leastSquares = function (values_x: number[], values_y: number[]) {
  let sum_x = 0;
  let sum_y = 0;
  let sum_xy = 0;
  let sum_xx = 0;
  let count = 0;

  let x = 0;
  let y = 0;
  const values_length = values_x.length;

  if (values_length != values_y.length) {
    throw new Error('The parameters values_x and values_y need to have same size!');
  }

  if (values_length === 0) {
    return [[], []];
  }

  for (let v = 0; v < values_length; v++) {
    x = values_x[v];
    y = values_y[v];
    sum_x += x;
    sum_y += y;
    sum_xx += x * x;
    sum_xy += x * y;
    count++;
  }
  const m = (count * sum_xy - sum_x * sum_y) / (count * sum_xx - sum_x * sum_x);
  const b = (sum_y / count) - (m * sum_x) / count;


  return { b, m };
};


export const createMonteCarloChart = function (scenario: ILccaCalculatedImperial,
                                               obj: any,
                                               monteCarloDraws: { [key: number]: IAlternativeResult[] },
                                               maxIterations: number) {
  // const monteCarloResults = obj.monteCarloResults;
  let queueLengthWarning = false;

  const monteCarloResults: {
    monteCarloSortedCosts: number[]
    monteCarloProcessedData: IMonteCarloProcessedData
  }[] = [];


  // Switch from results
  const dataArray: number[][] = [];

  // scenario.alternatives.forEach(function (alternative) {
  //   alternativeResults.push({
  //     // index: alternative.index,
  //     monteCarloSortedCosts: [],
  //     monteCarloProcessedData: {
  //       data: [],
  //       bins: [],
  //       lower: 0,
  //       upper: 0,
  //       mode: 0,
  //       median: 0,
  //       mean: 0,
  //       stDev: 0
  //     }
  //   });
  //
  //   // alternative.monteCarloSortedCosts = [];
  //   // alternative.mmonteCarloProcessedData = {};onteCarloProcessedData = {};
  //
  //   dataArray.push([]);
  // });

  // Fill Array with total costs, and check for queue length warnings
  // TODO: What does 'result' look like?
  Object.values(monteCarloDraws).forEach((result: IAlternativeResult[], j: number) => {
    const d: number[] = [];
    result.forEach((alternative: IAlternativeResult, i) => {
      d.push(alternative.userCosts + alternative.agencyCosts);
      if (result[0].queueLengthWarning === true) {
        queueLengthWarning = true;
      }
    });
    dataArray.push(d);
  });


  // TODO: Error handling
  // if (queueLengthWarning === true) {
  //     app.renderErrorMessages('alert_error', { message: 'Warning: The queue length resulting for one or more hours exceeds the maximum queue length specified. This means that a maximum user cost will be calculated for these hours and displayed results underestimate the actual user cost. Please consider revising your traffic data or lane closure strategy (e.g. AADT, growth rate of traffic) to mitigate this issue.', httpErrors: [] });
  // }

  function sortNumber(a: number, b: number) {
    return a - b;
  }

  // dataArray.forEach(function (dataset) {
  //   monteCarloResults.push({monteCarloSortedCosts: dataset.sort(sortNumber)});
  // });
  // const dataArray = [data1];
  const colors = pickColors(10);

  // Graphing Code
  const numBins = 20;
  const percentile = 90;
  let selected = NaN;
  const lowerPercentile = (1 - percentile / 100) / 2;
  const upperPercentile = (1 + percentile / 100) / 2;
  const processedData = [];


  const dataFlattened = dataArray.reduce((a, b) => a.concat(b));

  // Make SVG to display data
  const svg = d3.select('#monteCarloResultGraph');
  const margin = {
    upper: 30,
    right: 20,
    lower: 60,
    left: 60,
  };
  // width = svg.attr("width") - margin.left - margin.right,
  // @ts-ignore
  const width = document.getElementById('monteCarloPlotContainer').offsetWidth - (margin.right + margin.left);
  // @ts-ignore
  const height = document.getElementById('monteCarloPlotContainer').offsetHeight - (margin.upper + margin.lower);
  // height = svg.attr("height") - margin.upper - margin.lower,
  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.upper})`);


  // Define x scale
  const x = d3.scaleLinear()
    .rangeRound([0, width])
    .domain([d3.min(dataFlattened) - ((d3.max(dataFlattened) - d3.min(dataFlattened)) * 0.1), d3.max(dataFlattened) + ((d3.max(dataFlattened) - d3.min(dataFlattened)) * 0.1)])
    .nice();

  // Process data
  for (let i = 0; i < scenario.alternatives.length; i += 1) {
    // const data = monteCarloResults[i].monteCarloSortedCosts;
    const sortedCosts = dataArray[i].sort(sortNumber);

    const localX = d3.scaleLinear()
      .rangeRound([0, width])
      .domain([d3.min(sortedCosts) - ((d3.max(sortedCosts) - d3.min(sortedCosts)) * 0.1), d3.max(sortedCosts) + ((d3.max(sortedCosts) - d3.min(sortedCosts)) * 0.1)])
      .nice();

    // Make histogram bins
    const bins: number[][] = d3.histogram()
      .domain(localX.domain())
      .thresholds(localX.ticks(numBins))(sortedCosts);

    // Find percentiles
    const lower = d3.quantile(sortedCosts, lowerPercentile);
    const upper = d3.quantile(sortedCosts, upperPercentile);

    // Find Mode
    const heights: number[] = [];
    bins.forEach((bin) => {
      heights.push(bin.length);
    });

    const modeIndex = heights.indexOf(heights.reduce((a, b) => Math.max(a, b)));
    const mode = bins[modeIndex];

    // Find Stats
    const mean = d3.mean(sortedCosts);
    const median = d3.median(sortedCosts);
    const stDev = d3.deviation(sortedCosts);


    // Make object
    // monteCarloResults[i].monteCarloProcessedData = {
    //   data: sortedCosts || [0],
    //   bins: bins,
    //   lower: lower,
    //   upper: upper,
    //   mode: mode,
    //   median: median,
    //   mean: mean,
    //   stDev: stDev,
    // }

    monteCarloResults.push({
      monteCarloProcessedData: {
        data: sortedCosts || [0],
        bins,
        lower,
        upper,
        mode,
        median,
        mean,
        stDev,
      },
      monteCarloSortedCosts: sortedCosts,
    });
  }

  function tallestBin() {
    const heights: number[] = [];
    monteCarloResults.forEach((alternativeResult) => {
      const modeLength = alternativeResult.monteCarloProcessedData ? alternativeResult.monteCarloProcessedData.mode.length : 0;
      const modeDataLength = alternativeResult.monteCarloProcessedData ? alternativeResult.monteCarloProcessedData.data.length : 0;
      heights.push(modeLength / modeDataLength);
    });

    return heights.reduce((a, b) => Math.max(a, b));
  }

  // Define y scale
  const y = d3.scaleLinear()
    .domain([0, tallestBin() * 1.25])
    .rangeRound([height, 0])
    .nice();

  // Legend rectangle
  const legendRect = g.selectAll('rect')
    .data(scenario.alternatives)
    .enter()
    .append('rect')
    .attr('class', 'legend')
    .attr('fill-opacity', 0.2)
    .attr('fill', (d: any, i: number) => colors[i])
    .attr('stroke', (d: any, i: number) => colors[i])
    .attr('stroke-width', 2)
    .attr('x', 1)
    .attr('width', 30)
    .attr('height', 30)
    .attr('transform', (d: any, i: number) => `translate(700, ${50 + 40 * i})`)
    .on('mouseover', selectAlternative)
    .on('mouseout', deselectAlternative);

  // Add text to legend
  const legendText = g.selectAll('text')
    .data(scenario.alternatives)
    .enter()
    .append('text')
    .attr('class', 'legend')
    .attr('x', 745)
    .attr('y', (d: any, i: number) => 70 + 40 * i)
    .text((d: any, i: number) => scenario.alternatives[i].name)
    .attr('font-family', 'Tahoma')
    .attr('font-size', '15px')
    .on('mouseover', selectAlternative)
    .on('mouseout', deselectAlternative);

  g.append('text')
    .attr('x', 705)
    .attr('y', 10)
    .attr('class', 'legend')
    .attr('font-family', 'Tahoma')
    .attr('font-weight', 'bold')
    .attr('font-size', '15px')
    .text(`${dataArray[0].length.toLocaleString()} runs out of ${maxIterations.toLocaleString()}`);

  displayCurves();
  displayAxes();

  // makeTable();

  function selectAlternative(d: any, i: number) {
    selected = i;
    clearCurves();
    displayAxes();
    displayCurves();
  }

  function deselectAlternative(d: any, i: number) {
    selected = NaN;
    clearCurves();
    displayAxes();
    displayCurves();
  }

  function displayCurves() {
    monteCarloResults.forEach((alternativeMonteCarloResults, i) => {
      if (isNaN(selected) || selected == i) {
        // Define line to display
        const line = d3.line()
          .curve(d3.curveBasis)
          .x((d: any) => x((d.x0 + d.x1) / 2))
          .y((d: any) => y((d.length) / alternativeMonteCarloResults.monteCarloProcessedData.data.length));

        // Add line to graph
        const path = g.append('path')
          .attr('fill', 'none')
          .attr('stroke', colors[i])
          .attr('stroke-width', 2)
          .attr('d', line(alternativeMonteCarloResults.monteCarloProcessedData.bins));

        // Add confidence interval and mode if selected
        if (selected == i) {
          g.append('g:rect')
            .attr('x', 1)
            .attr('fill', 'none')
            .attr('stroke', colors[i])
            .attr('stroke-width', 2)
            .style('stroke-dasharray', ('3, 5'))
            .attr('width', x(alternativeMonteCarloResults.monteCarloProcessedData.upper) - x(alternativeMonteCarloResults.monteCarloProcessedData.lower))
            .attr('height', height - 20)
            .attr('transform', `translate(${x(alternativeMonteCarloResults.monteCarloProcessedData.lower)},20)`);

          // Rectangle under confidence interval text
          g.append('rect')
            .attr('width', 100)
            .attr('height', 30)
            .attr('transform', `translate(${(x((alternativeMonteCarloResults.monteCarloProcessedData.lower + alternativeMonteCarloResults.monteCarloProcessedData.upper) / 2)) - 100 / 2}, 5)`)
            .attr('fill', 'white');

          // Confidence interval text
          g.append('text')
            .attr('class', 'ciText')
            .attr('x', x((alternativeMonteCarloResults.monteCarloProcessedData.lower + alternativeMonteCarloResults.monteCarloProcessedData.upper) / 2))
            .attr('y', 25)
            .attr('text-anchor', 'middle')
            .attr('font-family', 'Tahoma')
            .text('90% of Runs');

          // mode text
          g.append('text')
            .attr('class', 'ciText')
            .attr('y', y(alternativeMonteCarloResults.monteCarloProcessedData.mode.length / alternativeMonteCarloResults.monteCarloProcessedData.data.length) - 5)
            .attr('x', x((alternativeMonteCarloResults.monteCarloProcessedData.mode.x0 + alternativeMonteCarloResults.monteCarloProcessedData.mode.x1) / 2))
            .attr('text-anchor', 'middle')
            .attr('font-family', 'Tahoma')
            .text(`Mode: ${(alternativeMonteCarloResults.monteCarloProcessedData.mode.x0).toLocaleString()}-${(alternativeMonteCarloResults.monteCarloProcessedData.mode.x1).toLocaleString()}`);
        }

        // Create clip with percentile cutoffs for confidence interval
        const clip1 = svg.append('g:clipPath')
          .attr('id', `clip${i + 1}`)
          .append('g:rect')
          .attr('x', 1)
          .attr('width', x(alternativeMonteCarloResults.monteCarloProcessedData.upper) - x(alternativeMonteCarloResults.monteCarloProcessedData.lower))
          .attr('height', height)
          .attr('transform', `translate(${x(alternativeMonteCarloResults.monteCarloProcessedData.lower)},0)`);

        // Filled area for confidence interval
        g.append('path')
          .attr('fill-opacity', 0.2)
          .attr('fill', colors[i])
          .attr('clip-path', `url(#clip${i + 1})`)
          .attr('d', line(alternativeMonteCarloResults.monteCarloProcessedData.bins));
      }
    });
  }

  function displayAxes() {
    // X-axis
    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x));

    g.select('.x-axis')
      .selectAll('text')
      .style('font-size', '12px')
      .attr('font-family', 'Tahoma');

    // Add x axis label
    g.append('text')
      .attr('transform',
        `translate(${width / 2} ,${
          height + margin.upper + 20})`)
      .style('text-anchor', 'middle')
      .style('font-size', '16px')
      .attr('font-family', 'Tahoma')
      .text('Total Cost ($1000)');

    // Add y axis
    g.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(y).ticks(6, '.0%'));

    g.select('.y-axis')
      .selectAll('text')
      .style('font-size', '12px')
      .attr('font-family', 'Tahoma');

    // y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - (height / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', '16px')
      .attr('font-family', 'Tahoma')
      .text('Percent of Runs');
  }

  function clearCurves() {
    d3.selectAll('g :not(.legend)').remove();
  }


  return monteCarloResults;

  // function makeTable() {
  //   scenario.alternatives.forEach(function (alternative) {
  //     $("#probResultsTable").append(
  //       '               <tbody>' +
  //       '	                <tr>' +
  //       '	                    <th>' + alternative.name + '</th>' +
  //       '	                    <td>' + Math.round(alternative.monteCarloProcessedData.mode.x0).toLocaleString() + "-" + Math.round(alternative.monteCarloProcessedData.mode.x1).toLocaleString() + '</td>' +
  //       '	                    <td>' + Math.round(alternative.monteCarloProcessedData.lower).toLocaleString() + "-" + Math.round(alternative.monteCarloProcessedData.upper).toLocaleString() + '</td>' +
  //       '	                    <td>' + Math.round(alternative.monteCarloProcessedData.mean).toLocaleString() + '</td>' +
  //       '	                    <td>' + Math.round(alternative.monteCarloProcessedData.stDev).toLocaleString() + '</td>' +
  //       '	                </tr>' +
  //       '	            </tbody>'
  //     );
  //   });

  // }
};
